import React from 'react';

import { CardVariantContext } from 'components/RegistrationPrototype/d4Primitives';

import { judgeRelease, resistDrag, scrimProgress } from './sheetDrag';

/**
 * The presentation host: a REAL bottom drawer on mobile, a centred dialog on
 * desktop.
 *
 * Design pass 2026-08-19 (Jason), after driving the first version on device:
 * a drawer that cannot be dragged is a picture of a drawer. The grabber has to
 * work. So this tracks the thumb, fades the scrim as the sheet falls — the
 * graduated reveal of the content behind — and judges the release on intent
 * (see sheetDrag.ts) rather than distance alone.
 *
 * Also fixed here, all from the same device pass:
 *
 *   - **The sheet now meets the bottom of the display.** It used to be pushed
 *     up by `padding-bottom: env(safe-area-inset-bottom)`, leaving a black band
 *     under it and the card's orange ring visible along its lower edge. A
 *     drawer is anchored to the screen edge; a floating card with a bottom
 *     border is a different object. The card is pulled down past the inset so
 *     its bottom edge and ring sit off-screen, and its own background fills the
 *     home-indicator area.
 *   - **Tap outside dismisses; tap inside never does.**
 *   - **Reduced motion** collapses the entrances to a fade and skips the spring
 *     settle, but keeps the drag itself — a gesture is not decoration.
 */

/**
 * Exit duration. Was 220ms; Jason asked for ~20% slower after driving it on
 * device — at 220 the drawer left before the eye could follow the scrim
 * clearing with it, so the "graduated reveal" read as a cut rather than a
 * reveal. 265ms is still inside the 200–500ms band a drawer gets.
 *
 * Everything that moves on exit — sheet, scrim, blur — is derived from this one
 * number, so they cannot drift apart. The scrim is deliberately 25ms shorter:
 * the blur wants to be gone by the time the sheet clears the edge, or the app
 * behind sits soft for a beat after the drawer has left.
 */
const EXIT_MS = 265;
const SCRIM_MS = EXIT_MS - 25;

const SPRING_BACK = 'transform 280ms cubic-bezier(0.2, 0.9, 0.24, 1)';
const FLING_OUT = `transform ${EXIT_MS}ms cubic-bezier(0.4, 0, 1, 1), opacity ${SCRIM_MS}ms linear`;

export const BIOMETRIC_COMPACT_MAX_PX = 767;

export function resolveBiometricPresentation(
  requested: 'auto' | 'sheet' | 'modal',
  opts: { native: boolean; viewportWidth: number }
): 'sheet' | 'modal' {
  if (requested !== 'auto') return requested;
  return opts.native || opts.viewportWidth <= BIOMETRIC_COMPACT_MAX_PX ? 'sheet' : 'modal';
}

/**
 * Lets any control inside the sheet dismiss it through the SAME exit as the
 * drag: sheet translates down, scrim and blur fade with it.
 *
 * Without this, only the gesture animated. "Not now" — the most-used exit —
 * unmounted the surface instantly, so the drawer blinked out of existence
 * while a drag of the identical distance glided (Jason, on-device
 * 2026-08-19). One dismissal, one animation, however it was triggered.
 *
 * Returns a passthrough outside a host, so the cards work unwrapped in
 * Storybook grids.
 */
type SheetControl = {
  dismiss: (after?: () => void) => void;
  /**
   * True once the player has put a finger on the sheet.
   *
   * Timed dismissals (the enrolment confirmation) use this to stand down. A
   * drawer that slides away while it is being held is the worst version of a
   * timer: the gesture and the clock fight, and the clock wins for no reason
   * the player can see. Touching the sheet is an explicit claim on it, so the
   * timer yields — same instinct as a toast pausing under the cursor.
   */
  engaged: boolean;
};

const SheetControlContext = React.createContext<SheetControl | null>(null);

export function useSheetDismiss(): (after?: () => void) => void {
  const ctx = React.useContext(SheetControlContext);
  return React.useMemo(() => ctx?.dismiss ?? ((after?: () => void) => after?.()), [ctx]);
}

/** See `SheetControl.engaged`. False outside a host, so timers still run. */
export function useSheetEngaged(): boolean {
  return React.useContext(SheetControlContext)?.engaged ?? false;
}

/**
 * Tint the NATIVE status bar to match the scrim while an overlay is up.
 *
 * The app sets `overlaysWebView: false` on purpose (see capacitor.config.ts —
 * it stops the clock overlapping top-pinned UI on devices where
 * safe-area-inset-top reports 0). The consequence is that the status bar is
 * native chrome the WebView cannot draw into, so a full-screen scrim stops at
 * the WebView's top edge and leaves a hard line under the notch
 * (Jason, on-device 2026-08-19).
 *
 * CSS cannot reach those pixels. What we can do is paint the native bar the
 * colour the scrim composites to over the app background —
 * rgba(3,7,14,.6) over #12100c ≈ #090b0d — so the seam disappears. Restored on
 * unmount.
 *
 * NOTE for whoever picks this up: every full-screen overlay in the app has the
 * same seam. The general fix is `overlaysWebView: true` plus honouring
 * safe-area insets app-wide, which is a bigger change than this ticket and has
 * the Android caveat the config comment describes. This is the local remedy.
 */
const SCRIM_STATUS_BAR = '#090b0d';
const APP_STATUS_BAR = '#12100c';

function useScrimmedStatusBar(active: boolean) {
  React.useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const { StatusBar } = await import('@capacitor/status-bar');
        if (cancelled) return;
        await StatusBar.setBackgroundColor({ color: SCRIM_STATUS_BAR });
      } catch {
        /* web, or the plugin is unavailable — the seam simply stays */
      }
    })();
    return () => {
      cancelled = true;
      void (async () => {
        try {
          const { StatusBar } = await import('@capacitor/status-bar');
          await StatusBar.setBackgroundColor({ color: APP_STATUS_BAR });
        } catch {
          /* noop */
        }
      })();
    };
  }, [active]);
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

/** Matches the WAI-ARIA APG modal dialog pattern's notion of "tabbable". */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function BiometricSurfaceHost({
  children,
  presentation = 'auto',
  native,
  contained,
  dismissible = true,
  onDismiss,
  ariaLabel = 'Biometric sign-in'
}: {
  children: React.ReactNode;
  presentation?: 'auto' | 'sheet' | 'modal';
  /** Defaults to Capacitor's answer; overridable so stories can show both. */
  native?: boolean;
  /** Position within the nearest positioned ancestor. Stories only. */
  contained?: boolean;
  /** False while an OS prompt is up — dragging it away mid-scan is nonsense. */
  dismissible?: boolean;
  onDismiss?: () => void;
  /** Accessible name for the dialog — the cards inside carry no heading markup. */
  ariaLabel?: string;
}) {
  const [width, setWidth] = React.useState(
    typeof window === 'undefined' ? 1024 : window.innerWidth
  );
  React.useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isNative =
    native ?? (typeof window !== 'undefined' && !!(window as { Capacitor?: unknown }).Capacitor);
  const variant = resolveBiometricPresentation(presentation, {
    native: isNative,
    viewportWidth: width
  });
  const sheet = variant === 'sheet';
  // Only while the overlay is actually covering the app.
  useScrimmedStatusBar(isNative && !contained);

  const sheetRef = React.useRef<HTMLDivElement>(null);
  const drag = React.useRef<{ startY: number; lastY: number; lastT: number; v: number } | null>(
    null
  );
  const [offset, setOffset] = React.useState(0);
  const [settling, setSettling] = React.useState<string | undefined>(undefined);
  const [leaving, setLeaving] = React.useState(false);
  const [engaged, setEngaged] = React.useState(false);

  const height = () => sheetRef.current?.getBoundingClientRect().height || 1;

  /**
   * The single exit. `after` runs once the surface is gone, so the caller's
   * state change (unmounting the card, flipping a flag) never races the
   * animation and cuts it short.
   */
  const animateOut = React.useCallback(
    (after?: () => void) => {
      const finish = after ?? onDismiss;
      if (leaving) return;
      if (prefersReducedMotion()) {
        finish?.();
        return;
      }
      setLeaving(true);
      setSettling(FLING_OUT);
      if (sheet) setOffset(height());
      window.setTimeout(() => finish?.(), EXIT_MS);
    },
    [leaving, onDismiss, sheet]
  );

  // Modal focus management (WAI-ARIA APG dialog pattern): move focus into the
  // surface on mount, trap it there, and give it back to whatever had it
  // before — a keyboard/AT user landing on a control behind the scrim mid
  // authentication decision is exactly the "focus escapes the modal" bug this
  // fixes.
  React.useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const node = sheetRef.current;
    const firstFocusable = node?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (firstFocusable ?? node)?.focus();
    return () => {
      previouslyFocused?.focus?.();
    };
    // Mount/unmount only — the host stays mounted while its card content
    // switches (e.g. BiometricUnlockPrompt swapping cards under one host), and
    // that swap must not steal focus back from wherever the player moved it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (!dismissible) return;
      e.stopPropagation();
      animateOut();
      return;
    }
    if (e.key !== 'Tab') return;
    const node = sheetRef.current;
    if (!node) return;
    const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusables.length === 0) {
      e.preventDefault();
      node.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey ? active === first || !node.contains(active) : active === last || !node.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    // Before any early return: a touch that lands on a button still counts as
    // the player taking charge of the surface.
    setEngaged(true);
    if (!sheet || !dismissible) return;
    // Let controls keep their taps: only bare surface starts a drag.
    if ((e.target as HTMLElement).closest('button, a, input, [role="switch"]')) return;
    drag.current = { startY: e.clientY, lastY: e.clientY, lastT: e.timeStamp, v: 0 };
    setSettling(undefined);
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dt = Math.max(1, e.timeStamp - d.lastT);
    d.v = (e.clientY - d.lastY) / dt;
    d.lastY = e.clientY;
    d.lastT = e.timeStamp;
    setOffset(resistDrag(e.clientY - d.startY));
  };

  const endDrag = () => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    const { dismiss } = judgeRelease(offset, d.v, height());
    if (dismiss && onDismiss) {
      animateOut();
      return;
    }
    setSettling(prefersReducedMotion() ? undefined : SPRING_BACK);
    setOffset(0);
  };

  // Scrim tracks the drag, so the app behind is progressively revealed.
  const scrimAlpha = sheet ? scrimProgress(Math.max(0, offset), height()) : 1;

  const control = React.useMemo<SheetControl>(
    () => ({ dismiss: animateOut, engaged }),
    [animateOut, engaged]
  );

  return (
    <CardVariantContext.Provider value={variant}>
      <SheetControlContext.Provider value={control}>
        <div
          style={{
            position: contained ? 'absolute' : 'fixed',
            inset: 0,
            zIndex: 1600,
            display: 'flex',
            alignItems: sheet ? 'flex-end' : 'center',
            justifyContent: 'center',
            padding: sheet ? 0 : '0 16px',
            background: `rgba(3,7,14,${0.6 * scrimAlpha})`,
            backdropFilter: `blur(${14 * scrimAlpha}px)`,
            WebkitBackdropFilter: `blur(${14 * scrimAlpha}px)`,
            // No transition while the finger is down — the scrim must track the
            // gesture frame-for-frame, not lag behind it.
            transition: drag.current
              ? undefined
              : `background ${SCRIM_MS}ms linear, opacity ${EXIT_MS}ms linear, backdrop-filter ${EXIT_MS}ms linear`,
            opacity: leaving ? 0 : 1
          }}
          onClick={dismissible ? () => animateOut() : undefined}
          onKeyDown={onKeyDown}>
          <div
            ref={sheetRef}
            role='dialog'
            aria-modal='true'
            aria-label={ariaLabel}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            style={{
              width: sheet ? '100%' : undefined,
              display: 'flex',
              justifyContent: 'center',
              transform: `translateY(${sheet ? offset : 0}px)`,
              transition: settling,
              touchAction: sheet ? 'none' : undefined,
              // A drawer is anchored to the screen edge, so it is pulled down
              // past the home-indicator inset: its own background then fills the
              // safe area instead of leaving a black band under the sheet.
              marginBottom: sheet ? 'calc(-1 * env(safe-area-inset-bottom, 0px) - 2px)' : undefined,
              animation:
                sheet && !prefersReducedMotion()
                  ? 'bioSheetIn 340ms cubic-bezier(0.2, 0.9, 0.24, 1) both'
                  : 'bioFadeIn 200ms ease both'
            }}>
            {children}
          </div>
        </div>
      </SheetControlContext.Provider>
    </CardVariantContext.Provider>
  );
}
